'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var Button_parts = require('./Button.parts.js');

/**
 * 다양한 스타일과 기능을 제공하는 버튼 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {'button' | 'submit' | 'reset'} [props.type='button'] - 버튼의 타입
 * @param {React.RefObject<HTMLButtonElement>} [props.ref] - 버튼 요소에 대한 ref
 * @param {boolean} [props.disabled=false] - 버튼 비활성화 여부
 * @param {React.ReactNode} props.children - 버튼 내부 콘텐츠
 * @param {string} [props.className] - 추가 CSS 클래스
 * @param {function} [props.onClick] - 클릭 이벤트 핸들러
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - 버튼 크기
 * @param {'outlined' | 'solid' | 'outline_filled'} [props.variant='outlined'] - 버튼 스타일 변형
 * @param {'primary' | 'gray'} [props.color='primary'] - 버튼 색상, outlined 일 때에만 적용 가능
 * @param {boolean} [props.rounded=false] - 둥근 모서리 적용 여부
 * @param {React.ReactNode} [props.leadingIcon] - 버튼 앞쪽 아이콘
 * @param {boolean} [props.isLeadingIconSolid=false] - 앞쪽 아이콘 solid 스타일 적용 여부
 * @param {React.ReactNode} [props.trailingIcon] - 버튼 뒤쪽 아이콘
 * @param {boolean} [props.isTrailingIconSolid=false] - 뒤쪽 아이콘 solid 스타일 적용 여부
 * @param {string | number} [props.badgeNumber=''] - 버튼에 표시될 뱃지 숫자
 * @param {boolean} [props.needThrottle=false] - 쓰로틀링 적용 여부
 *
 * @example
 * // 기본 버튼
 * <Button onClick={() => console.log('클릭됨')}>
 *   버튼
 * </Button>
 *
 * // 아이콘이 있는 비활성화된 버튼
 * <Button
 *   disabled
 *   variant="contained"
 *   leadingIcon={<Icon name="search" />}
 *   size="lg"
 * >
 *   검색
 * </Button>
 *
 * // 쓰로틀링이 적용된 둥근 버튼
 * <Button
 *   rounded
 *   needThrottle
 *   variant="text"
 *   badgeNumber="5"
 *   onClick={handleClick}
 * >
 *   알림
 * </Button>
 */ const Button = (_a)=>{
    var { type = 'button', ref, disabled = false, children, className, onClick, size = 'md', variant = 'outlined', color = 'primary', rounded = false, leadingIcon, isLeadingIconSolid = false, trailingIcon, isTrailingIconSolid = false, badgeNumber = '', needThrottle = false } = _a, rest = _tslib.__rest(_a, [
        "type",
        "ref",
        "disabled",
        "children",
        "className",
        "onClick",
        "size",
        "variant",
        "color",
        "rounded",
        "leadingIcon",
        "isLeadingIconSolid",
        "trailingIcon",
        "isTrailingIconSolid",
        "badgeNumber",
        "needThrottle"
    ]);
    // eslint-disable-next-line no-undef
    const timerId = react.useRef(undefined);
    const throttledOnClick = (e)=>{
        e.preventDefault();
        if (timerId.current) return;
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        timerId.current = setTimeout(()=>{
            timerId.current = undefined;
        }, 300);
    };
    react.useEffect(()=>{
        return ()=>{
            if (timerId) {
                clearTimeout(timerId.current);
            }
        };
    }, [
        timerId
    ]);
    return jsxRuntime.jsx(Button_parts.ButtonContainer, Object.assign({
        ref: ref,
        size: size,
        type: type,
        disabled: disabled,
        rounded: rounded,
        color: color,
        variant: variant,
        className: className,
        onClick: needThrottle ? throttledOnClick : onClick,
        leadingIcon: leadingIcon,
        isLeadingIconSolid: isLeadingIconSolid,
        trailingIcon: trailingIcon,
        isTrailingIconSolid: isTrailingIconSolid,
        badgeNumber: badgeNumber
    }, rest, {
        children: children
    }));
};

module.exports = Button;
